import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface BreadcrumbCrumb {
  label: string;
  href?: string;
}

interface BreadcrumbsContextValue {
  override: BreadcrumbCrumb[] | null;
  setOverride: (crumbs: BreadcrumbCrumb[] | null) => void;
}

const BreadcrumbsContext = createContext<BreadcrumbsContextValue>({
  override: null,
  setOverride: () => {},
});

export function BreadcrumbsProvider({ children }: { children: React.ReactNode }) {
  const [override, setOverride] = useState<BreadcrumbCrumb[] | null>(null);
  const value = useMemo(() => ({ override, setOverride }), [override]);
  return (
    <BreadcrumbsContext.Provider value={value}>
      {children}
    </BreadcrumbsContext.Provider>
  );
}

export function useBreadcrumbsOverride(): BreadcrumbCrumb[] | null {
  return useContext(BreadcrumbsContext).override;
}

// Pages call this hook to install a custom breadcrumb trail. The hook
// clears the override on unmount so the next page's URL-derived default
// renders cleanly. Stringified-deps comparison avoids the caller having
// to memoise the array literal at every render.
export function useBreadcrumbs(crumbs: BreadcrumbCrumb[] | null): void {
  const { setOverride } = useContext(BreadcrumbsContext);
  const key = JSON.stringify(crumbs);
  const stableSet = useCallback(setOverride, [setOverride]);
  const crumbsRef = useRef(crumbs);
  crumbsRef.current = crumbs;
  useEffect(() => {
    stableSet(crumbsRef.current);
    return () => stableSet(null);
  }, [key, stableSet]);
}
